import { useMemo } from 'react'

import { ButtonRebrandVariant } from 'src/components/atoms/ButtonRebrand'
import { SanityImage } from 'src/components/atoms/SanityImage'
import TypographyRebrand from 'src/components/atoms/TypographyRebrand/TypographyRebrand'
import {
  CmsCtaButtonRebrand,
  CmsCtaButtonRebrandProps
} from 'src/components/molecules/CmsCtaButtonRebrand'

export interface TileRebrandProps {
  heading?: string
  description?: string
  icon?: Parameters<typeof SanityImage>[0]['source']
  button?: Omit<
    CmsCtaButtonRebrandProps,
    'colorVariant' | 'className' | 'text'
  > & {
    text?: string
  }
}

export interface TilesSectionRebrandProps {
  heading?: string
  color?: string
  tiles?: TileRebrandProps[]
}

const getTileBg = (color?: string) => {
  switch (color) {
    case 'lime':
      return 'bg-limeFresh/50'
    case 'lavender':
      return 'bg-lavenderBright/50'
    case 'white':
      return 'bg-white'
    case 'cream':
    default:
      return 'bg-cream/50'
  }
}

const getButtonVariant = (color?: string): ButtonRebrandVariant => {
  switch (color) {
    case 'lime':
      return 'limeFresh'
    case 'lavender':
      return 'lavenderBright'
    case 'white':
      return 'lime'
    case 'cream':
    default:
      return 'cream'
  }
}

export const TilesSectionRebrand = ({
  heading,
  tiles = [],
  color
}: TilesSectionRebrandProps) => {
  const tileBg = getTileBg(color)
  const buttonVariant = getButtonVariant(color)

  const gridClasses = useMemo(() => {
    switch (tiles.length) {
      case 1:
        return 'lg:grid-cols-1'
      case 2:
        return 'lg:grid-cols-2'
      case 3:
      case 6:
        return 'lg:grid-cols-3'
      default:
        return 'lg:grid-cols-2'
    }
  }, [tiles])

  return (
    <div className='container mx-auto px-4 lg:px-20'>
      <div className='flex flex-col gap-5'>
        {heading && (
          <TypographyRebrand variant='h4' color='forestDeep'>
            {heading}
          </TypographyRebrand>
        )}
        <div className={`grid grid-cols-1 gap-4 md:gap-6 ${gridClasses}`}>
          {tiles.map((tile, idx) => (
            <div
              key={`tile_${idx}`}
              className={`flex flex-col items-start justify-between gap-12 px-8 py-6 md:py-12 ${tileBg}`}
            >
              <div className='flex w-full flex-col items-start gap-6 md:gap-8'>
                {tile.icon && (
                  <div className='h-[86px] w-[86px]'>
                    <SanityImage
                      source={tile.icon}
                      alt=''
                      width={86}
                      height={86}
                      className='h-full w-full object-contain object-left'
                    />
                  </div>
                )}
                <div className='flex w-full flex-col gap-2'>
                  {tile.heading && (
                    <TypographyRebrand variant='h5' color='forestDeep'>
                      {tile.heading}
                    </TypographyRebrand>
                  )}
                  {tile.description && (
                    <TypographyRebrand variant='paragraph' color='forestDeep'>
                      {tile.description}
                    </TypographyRebrand>
                  )}
                </div>
              </div>
              {tile.button?.text && (
                <CmsCtaButtonRebrand
                  text={tile.button.text}
                  linkType={tile.button.linkType}
                  actionType={tile.button.actionType}
                  internalLink={tile.button.internalLink}
                  externalUrl={tile.button.externalUrl}
                  colorVariant={buttonVariant}
                  className='w-full'
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default TilesSectionRebrand
